import {
  ADMIN_PERMISSIONS,
  PORTAL_MULTAS_ROUTE,
  REPORT_PERMISSIONS,
  ROUTE_PERMISSION_PREFIXES,
  SCHEDULE_PERMISSIONS,
  VEHICLE_MANAGEMENT_PERMISSIONS,
} from './portalPermissionConfig';

/**
 * Fail-closed permission check for portal menu and route guards.
 * Null, undefined, or empty permissions grant access to nothing.
 */
export const hasPortalPermission = (
  permissions: Permission[] | null | undefined,
  permission: Permission,
): boolean => {
  if (!permissions?.length) return false;
  return permissions.includes(permission);
};

export const hasAnyPortalPermission = (
  permissions: Permission[] | null | undefined,
): boolean => Boolean(permissions?.length);

export const hasAnyPermissionFrom = (
  permissions: Permission[] | null | undefined,
  required: ReadonlyArray<Permission>,
): boolean =>
  required.some(permission => hasPortalPermission(permissions, permission));

export const hasAnyReportPermission = (
  permissions: Permission[] | null | undefined,
): boolean => hasAnyPermissionFrom(permissions, REPORT_PERMISSIONS);

export const hasAnySchedulePermission = (
  permissions: Permission[] | null | undefined,
): boolean => hasAnyPermissionFrom(permissions, SCHEDULE_PERMISSIONS);

export const hasAnyVehicleManagementPermission = (
  permissions: Permission[] | null | undefined,
): boolean => hasAnyPermissionFrom(permissions, VEHICLE_MANAGEMENT_PERMISSIONS);

export const hasAnyAdminPermission = (
  permissions: Permission[] | null | undefined,
): boolean => hasAnyPermissionFrom(permissions, ADMIN_PERMISSIONS);

const normalizePortalPath = (path: string): string => {
  const withoutQuery = path.split('?')[0]?.split('#')[0] ?? path;
  if (/^https?:\/\//i.test(withoutQuery)) return withoutQuery;
  const normalized = withoutQuery.startsWith('/')
    ? withoutQuery
    : `/${withoutQuery}`;
  return normalized.replace(/\/+$/, '') || '/';
};

export const isPortalPathAllowed = (
  path: string,
  permissions: Permission[] | null | undefined,
): boolean => {
  const normalized = normalizePortalPath(path);

  if (/^https?:\/\//i.test(normalized)) {
    return hasPortalPermission(permissions, 'vamosControl');
  }

  if (normalized === '/' || normalized === '/login' || normalized === '/sem-acesso') {
    return true;
  }

  if (
    normalized === PORTAL_MULTAS_ROUTE ||
    normalized.startsWith(`${PORTAL_MULTAS_ROUTE}/`)
  ) {
    return hasAnyPortalPermission(permissions);
  }

  const rule = ROUTE_PERMISSION_PREFIXES.find(({ prefix }) =>
    normalized === prefix || normalized.startsWith(`${prefix}/`),
  );

  if (!rule) return true;

  return hasAnyPermissionFrom(permissions, rule.permissions);
};

/** Priority order mirrors SideContent menu (top → bottom, sub-items left → right). */
export const POST_LOGIN_ROUTE_PRIORITY: ReadonlyArray<{
  permission: Permission;
  path: string;
}> = [
  { permission: 'home', path: '/inicio' },
  { permission: 'models', path: '/relatorios/modelos' },
  { permission: 'fleetDist', path: '/relatorios/distdefrotas' },
  { permission: 'entraceExit', path: '/relatorios/manutencao/entradasaida' },
  { permission: 'services', path: '/relatorios/manutencao/servicos' },
  { permission: 'createSchedule', path: '/agendamentos' },
  { permission: 'scheduleList', path: '/agendamentos' },
  { permission: 'multiple', path: '/agendamentos' },
  { permission: 'callCenter', path: '/chamados' },
  { permission: 'maintenanceServices', path: '/servicos-manutencao' },
  { permission: 'finan', path: '/faturamento' },
  {
    permission: 'vamosControl',
    path: 'https://www.vamoscontrole.com.br/iweb2/loginvamoscontrole.aspx',
  },
  { permission: 'faq', path: '/centraldeduvidas' },
  { permission: 'crlv', path: '/gestao/veiculos' },
  { permission: 'damageCharge', path: '/gestao/cobrancas' },
  { permission: 'logs', path: '/administrativo/logs' },
  { permission: 'communique', path: '/administrativo/comunicados' },
  { permission: 'access', path: '/acessos' },
];

export const getFirstAllowedPortalPath = (
  permissions: Permission[] | null | undefined,
): string | null => {
  if (!hasAnyPortalPermission(permissions)) return null;

  const match = POST_LOGIN_ROUTE_PRIORITY.find(({ permission }) =>
    hasPortalPermission(permissions, permission),
  );

  return match?.path ?? null;
};

export const getPostLoginPath = (
  permissions: Permission[] | null | undefined,
  preferredPage?: string | null,
): string => {
  if (!hasAnyPortalPermission(permissions)) return '/sem-acesso';

  if (
    preferredPage &&
    preferredPage !== '/login' &&
    preferredPage !== '/sem-acesso' &&
    isPortalPathAllowed(preferredPage, permissions)
  ) {
    return preferredPage;
  }

  return getFirstAllowedPortalPath(permissions) ?? '/sem-acesso';
};

export {
  ADMIN_PERMISSIONS,
  REPORT_PERMISSIONS,
  SCHEDULE_PERMISSIONS,
  VEHICLE_MANAGEMENT_PERMISSIONS,
} from './portalPermissionConfig';
